import clsx from "clsx";
import { useEffect, useState } from "react";

import { Icon } from "../../../../assets/icons/Icon";
import { formatNumber, isRtl, pluralize } from "../../../../helpers/helpers";
import FormLabel from "../../../login/formLabel/FormLabel";
import Button from "../../../mantine/button/Button";
import Switch from "../../../switch/Switch";
import Modal from "../../../ui/modal/Modal";
import Spinner from "../../../ui/spinner/Spinner";
import TextArea from "../../../ui/textArea/TextArea";
import { ApprovePayload } from "../../onPage/types";
import styles from "./Image.module.scss";

interface ImageItem {
  src: string;
  id?: number | string;
  path?: string;
  appearances?: number;
  active?: boolean;
  suggestion?: string;
  final?: string;
  url?: string;
  original?: string;
  existing?: string;
  description?: string;
  inner_text?: string;
  tag?: string;
  page_id?: number;
  approved?: boolean;
  category_id?: number;
  element?: string;
  html?: string;
  lang: string | null | undefined;
}

interface EditedSuggestion {
  final: string;
  active: boolean;
  lang: string | null | undefined;
}

interface ContentSchemaModalProps {
  editedSuggestion: EditedSuggestion;
  setEditedSuggestion: React.Dispatch<React.SetStateAction<EditedSuggestion>>;
}

interface ImageProps {
  data: ImageItem[];
  approve: (data: ApprovePayload) => void;
  isLoading: boolean;
  editPageSuggestion?: (data: { id: number; final: string }) => void;
  isWidgetActionLoading?: boolean;
}

const ContentSchemaModal = ({
  editedSuggestion,
  setEditedSuggestion,
}: ContentSchemaModalProps) => {
  const lang = editedSuggestion.lang;

  return (
    <div className={styles.editSuggestionContent}>
      <form>
        <FormLabel label="Image Alt Text" />
        <div
          className={clsx({ ["rtl"]: isRtl(lang) })}
          lang={lang || undefined}
        >
          <TextArea
            minRows={6}
            value={editedSuggestion.final}
            onChange={(e) =>
              setEditedSuggestion({
                ...editedSuggestion,
                final: e.target.value,
              })
            }
            placeholder="Edit Alt Text"
            style={{ width: "100%" }}
          />
        </div>
        <div className={styles.switchContainer}>
          <FormLabel label="Approve Changes " />
          <Switch
            checked={editedSuggestion.active}
            onChange={() => {
              const newActiveValue = !editedSuggestion.active;
              setEditedSuggestion((prev) => ({
                ...prev,
                active: newActiveValue,
              }));
            }}
          />
        </div>
      </form>
    </div>
  );
};

const Image: React.FC<ImageProps> = ({
  data,
  approve,
  isLoading,
  editPageSuggestion,
  isWidgetActionLoading,
}) => {
  const [lastClickedId, setLastClickedId] = useState<number | string | null>(
    null,
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editedSuggestion, setEditedSuggestion] = useState<EditedSuggestion>({
    final: "",
    active: false,
    lang: null,
  });
  const [currentEditItem, setCurrentEditItem] = useState<ImageItem | null>(
    null,
  );

  useEffect(() => {
    if (!isLoading && !isWidgetActionLoading) {
      setLastClickedId(null);
      setIsEditModalOpen(false);
    }
  }, [isLoading, isWidgetActionLoading]);

  const handleApprove = (detail: ImageItem, event: React.MouseEvent) => {
    event.stopPropagation();

    setLastClickedId(detail.id || null);

    approve({
      kind: detail.active ? "unapprove" : "approve",
      ids: typeof detail.id === "number" ? [detail.id] : undefined,
      variant: "automation",
    });
  };

  const handleEditClick = (item: ImageItem, event: React.MouseEvent) => {
    event.stopPropagation();

    if (!editPageSuggestion || typeof item.id !== "number") return;

    setCurrentEditItem(item);
    setEditedSuggestion({
      final: item.final || item.suggestion || "",
      active: !!item.active,
      lang: item.lang,
    });

    setIsEditModalOpen(true);
  };

  const handleModalSubmit = () => {
    if (
      !currentEditItem ||
      typeof currentEditItem.id !== "number" ||
      !editPageSuggestion
    )
      return;

    setLastClickedId(currentEditItem.id);

    // Handle approval change
    if (editedSuggestion.active !== currentEditItem.active) {
      approve({
        kind: editedSuggestion.active ? "approve" : "unapprove",
        ids: [currentEditItem.id],
        variant: "automation",
      });
    }

    if (
      editedSuggestion.final !== currentEditItem.final &&
      editedSuggestion.final !== currentEditItem.suggestion
    ) {
      editPageSuggestion({
        id: currentEditItem.id,
        final: editedSuggestion.final,
      });
    }

    setIsEditModalOpen(false);
  };

  return (
    <>
      <div className={styles.pageContainer}>
        <div className={styles.pageContent}>
          {data?.map((item, index) => {
            const imageUrl = item?.src;
            const isCurrentItemLoading =
              (isLoading && lastClickedId === item.id) ||
              (isWidgetActionLoading && lastClickedId === item.id);

            return (
              <div className={styles.imageCard} key={item.id || index}>
                <div className={styles.imageCardHeader}>
                  <div className={styles.imageCardHeaderTitle}>
                    Image
                    {item?.appearances &&
                      ` (${formatNumber(item.appearances)} ${pluralize("appearance", item.appearances)})`}
                  </div>
                  <div>
                    <div className={styles.imageCardIcons}>
                      <Button
                        variant="nohoverWithPadding"
                        onClick={(event) => handleEditClick(item, event)}
                        disabled={
                          !editPageSuggestion || typeof item.id !== "number"
                        }
                      >
                        <Icon iconName="EditIcon" width="1rem" height="1rem" />
                      </Button>
                      {isCurrentItemLoading ? (
                        <Spinner size="1rem" />
                      ) : (
                        <Button
                          variant="nohoverWithPadding"
                          onClick={(event) => handleApprove(item, event)}
                        >
                          <Icon
                            iconName={
                              item.active
                                ? "GreenCheckmarkIcon"
                                : "CloseIconCircled"
                            }
                            width="1rem"
                            height="1rem"
                          />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {imageUrl && (
                  <div className={styles.imageContainer}>
                    <img
                      src={imageUrl}
                      alt={item?.suggestion || "Image Preview"}
                      className={styles.responsiveImage}
                    />
                  </div>
                )}

                <div className={styles.imageDetails}>
                  <div className={styles.imageCardContentLabel}>Suggestion</div>
                  <div
                    className={clsx(styles.imageCardContentDescription, {
                      ["rtl"]:
                        isRtl(item?.lang) && (item?.final || item?.suggestion),
                    })}
                    lang={item?.lang || undefined}
                  >
                    {item?.final || item?.suggestion || "Blank"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Modal
        isLoading={isWidgetActionLoading || isLoading}
        isOpen={isEditModalOpen}
        closeModal={() => setIsEditModalOpen(false)}
        buttonLabel="Update"
        formId="editSuggestion"
        // @ts-expect-error TS2339 non-existing className
        modalLabel={<h3 className={styles.modalTitle}>Edit Image Alt Text</h3>}
        action={handleModalSubmit}
      >
        <ContentSchemaModal
          editedSuggestion={editedSuggestion}
          setEditedSuggestion={setEditedSuggestion}
        />
      </Modal>
    </>
  );
};

export default Image;
